import { Link, useRouter } from '@tanstack/react-router'
import { Calendar, MapPin, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'
import { formatEnumName, getInquiryStageColor, inquiryStageFromName } from '@/lib/enum-utils'
import { formatTimestamp } from '@/lib/date-utils'
import type { RenderedInquiry } from './render-data-payload'

/**
 * Clickable inquiry card rendered inline in the chat transcript for
 * `render_data`. Markup mirrors `dashboard/lead-card.tsx`'s `renderInquiryItem`:
 * a bordered row linking to the lead detail page, hover-preloaded, with a
 * stage badge.
 */
export function InquiryEntityCard({ inquiry }: { inquiry: RenderedInquiry }) {
  const router = useRouter()
  const stage = inquiryStageFromName(inquiry.stage)
  const createdAt = formatTimestamp(new Date(inquiry.createdAt))

  const handlePrefetch = () => {
    void router.preloadRoute({ to: '/leads/$id', params: { id: inquiry.id } })
  }

  return (
    <Link
      to="/leads/$id"
      params={{ id: inquiry.id }}
      data-slot="entity-card"
      data-entity-type="inquiry"
      className="block rounded-lg border p-3 hover:bg-muted/50 transition-colors"
      onMouseEnter={handlePrefetch}
    >
      <div className="flex items-start justify-between">
        <div className="space-y-1 min-w-0 flex-1">
          <p className="font-medium text-sm truncate">{inquiry.contactName}</p>
          <div className="space-y-0.5 text-xs text-muted-foreground">
            {inquiry.eventLocation && (
              <div className="flex items-center gap-1.5 min-w-0">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{inquiry.eventLocation}</span>
              </div>
            )}
            {inquiry.guestCount > 0 && (
              <div className="flex items-center gap-1.5 min-w-0">
                <Users className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{inquiry.guestCount} guests</span>
              </div>
            )}
            {createdAt && (
              <div className="flex items-center gap-1.5 min-w-0">
                <Calendar className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{createdAt}</span>
              </div>
            )}
          </div>
        </div>
        <Badge variant={getInquiryStageColor(stage)}>
          {formatEnumName(stage, InquiryStageEnum)}
        </Badge>
      </div>
    </Link>
  )
}
